// Always use relative API paths - nginx proxy handles routing to backend
const API_BASE_URL = '/api';

import type { Product, ProductFilters, ProductListResponse, CreateProductRequest, UpdateProductData } from '../types/product';
import type { CreateOrganizationData, UpdateOrganizationData } from '../types/organization';
import type { UserFormData } from '../schemas/userSchema';
import type { DocumentTemplate, DocumentTemplateFilters, DocumentTemplateStats, DocumentTemplateVersion, GeneratedDocument } from '../types/documentTemplate';
import type { FarmerFormData, GetFarmersResponse, GetFarmerResponse, CreateFarmerResponse, UpdateFarmerResponse, FarmerDocument } from '../types/farmer';
import type { QuoteFilters, QuoteListResponse, QuoteDetailResponse, CreateDraftQuoteRequest, CreateQuoteRequest, UpdateQuoteRequest, QuotationSummaryResponse } from '../types/quote';
import type { AdministrativeUnit } from '../types/administrativeUnit';
import type { 
  PremiumDueRequest, 
  PaymentReceivedRequest, 
  PremiumEarningRequest,
  PaymentBatchRequest,
  BulkEarningRequest,
  ClientTransactionOptions,
  InsuranceJournal,
  ClientBalance,
  ClientBalanceSummary,
  ClientReceivablesAging,
  ClientTransaction,
  CursorPaginationResponse,
  PaymentBatch,
  PremiumEarningCandidate,
  AccountMapping,
  Client,
  CostCenter,
  ReconciliationReport,
  CommissionSummary
} from '../types/Insurance';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    message: string;
    stack?: string;
  };
}

interface BackendUser {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  roles: string[];
  organization: {
    id: string;
    name: string;
    code: string;
    type: string;
    isActive: boolean;
  } | null;
  lastLoginAt?: string;
  emailVerifiedAt?: string;
}

interface LoginResponse {
  user: BackendUser;
  token: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface SignupCredentials {
  email: string;
  password: string;
  name: string;
  organizationId?: string;
}

interface Organization {
  id: string;
  name: string;
  code: string;
  type: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Role {
  id: string;
  name: string;
  label: string;
  description: string;
  hierarchy: number;
  isSystem: boolean;
  permissions: string[];
}

interface Permission {
  id: string;
  name: string;
  resource: string;
  action: string;
  conditions?: Record<string, unknown>;
  description: string;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    // Initialize token from localStorage if it exists
    this.token = localStorage.getItem('auth-token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth-token', token);
    } else {
      localStorage.removeItem('auth-token');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {};

    // Only set Content-Type to application/json if body is not FormData
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: 'An error occurred' };
        }

        console.error('🔍 Backend validation error after quotation:', JSON.stringify(errorData, null, 2));
        throw new ApiError(
          response.status,
          errorData.error?.message || errorData.message || 'An error occurred',
          errorData
        );
      }

      const data: ApiResponse<T> = await response.json();

      if (!data.success) {
        throw new ApiError(
          500,
          data.error?.message || 'API request failed',
          data.error
        );
      }

      return data.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network or other errors
      throw new ApiError(
        0,
        error instanceof Error ? error.message : 'Network error occurred'
      );
    }
  }

  private async requestBlobWithFilename(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {};

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Request failed';
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new ApiError(response.status, errorMessage);
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    // Set token for future requests
    this.setToken(response.token);
    
    return response;
  }

  async signup(credentials: SignupCredentials): Promise<LoginResponse> {
    // Use the default organization for new signups
    const signupData = {
      ...credentials,
      organizationId: credentials.organizationId || undefined,
    };

    const response = await this.request<LoginResponse>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(signupData),
    });
    
    // Set token for future requests
    this.setToken(response.token);
    
    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Even if logout fails on the server, clear local token
      console.warn('Logout request failed, clearing local token anyway', error);
    } finally {
      this.setToken(null);
    }
  }

  async getProfile(): Promise<{ user: BackendUser }> {
    return this.request<{ user: BackendUser }>('/auth/me');
  }

  // Email verification endpoints
  async verifyEmail(token: string): Promise<{ success: boolean; message: string; user?: { id: string; email: string; name: string; emailVerified: boolean } }> {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerificationEmail(): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/resend-verification', {
      method: 'POST',
    });
  }

  async getEmailVerificationStatus(): Promise<{ verified: boolean; tokenExpiry?: string; canResend: boolean }> {
    return this.request('/auth/email-verification-status');
  }

  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<{ success: boolean; message: string; user?: { id: string; email: string; name: string; emailVerified: boolean } }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async resendVerificationEmailForUser(userId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/users/${userId}/resend-verification`, {
      method: 'POST',
    });
  }

  async setPassword(token: string, password: string): Promise<{ success: boolean; message: string; data: { user: { id: string; email: string; name: string; emailVerified: boolean; roles: string[]; organization: { id: string; name: string; code: string; type: string; isActive: boolean } }; token: string } }> {
    return this.request('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async updateProfile(data: { name?: string; avatar?: string; email?: string }): Promise<{ user: BackendUser }> {
    return this.request<{ user: BackendUser }>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // User management endpoints
  async getUsers(options?: { scope?: 'all' }): Promise<{ users: BackendUser[]; scope: 'organization' | 'all'; totalCount: number }> {
    const params = new URLSearchParams();
    if (options?.scope) params.set('scope', options.scope);
    const endpoint = params.toString() ? `/users?${params}` : '/users';
    return this.request<{ users: BackendUser[]; scope: 'organization' | 'all'; totalCount: number }>(endpoint);
  }

  async getUserById(id: string): Promise<{ user: BackendUser }> {
    return this.request<{ user: BackendUser }>(`/users/${id}`);
  }

  async createUser(userData: UserFormData): Promise<{ user: BackendUser }> {
    return this.request<{ user: BackendUser }>('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(id: string, userData: UserFormData): Promise<{ user: BackendUser }> {
    return this.request<{ user: BackendUser }>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteUser(id: string): Promise<void> {
    return this.request<void>(`/users/${id}`, {
      method: 'DELETE'
    });
  }

  // Organization endpoints
  async getOrganizations(params?: { page?: number; limit?: number; search?: string; type?: string; isActive?: boolean }): Promise<{ organizations: Organization[]; pagination?: { total: number; page: number; limit: number; totalPages: number } }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
    const endpoint = searchParams.toString() ? `/organizations?${searchParams}` : '/organizations';
    return this.request<{ organizations: Organization[]; pagination?: { total: number; page: number; limit: number; totalPages: number } }>(endpoint);
  }

  async getOrganizationById(id: string): Promise<{ organization: Organization }> {
    return this.request<{ organization: Organization }>(`/organizations/${id}`);
  }

  async createOrganization(data: CreateOrganizationData): Promise<{ organization: Organization }> {
    return this.request<{ organization: Organization }>('/organizations', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateOrganization(id: string, data: UpdateOrganizationData): Promise<{ organization: Organization }> {
    return this.request<{ organization: Organization }>(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteOrganization(id: string): Promise<void> {
    return this.request<void>(`/organizations/${id}`, {
      method: 'DELETE'
    });
  }

  async uploadOrganizationLogo(id: string, file: File): Promise<{ organization: Organization }> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.request<{ organization: Organization }>(`/organizations/${id}/logo`, {
      method: 'POST',
      body: formData
    });
  }

  async getOrganizationsForSelection(params?: { search?: string; type?: string }): Promise<{ organizations: Organization[] }> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.type) searchParams.set('type', params.type);
    const endpoint = searchParams.toString() ? `/organizations/selection?${searchParams}` : '/organizations/selection';
    return this.request<{ organizations: Organization[] }>(endpoint);
  }

  // Role endpoints
  async getRoles(): Promise<{ roles: Role[] }> {
    return this.request<{ roles: Role[] }>('/roles');
  }

  async getPermissions(): Promise<{ permissions: Permission[] }> {
    return this.request<{ permissions: Permission[] }>('/roles/permissions');
  }

  async createRole(data: { name: string; label: string; description?: string; hierarchy: number; permissions: string[] }): Promise<{ role: Role }> {
    return this.request<{ role: Role }>('/roles', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateRole(id: string, data: { name?: string; label?: string; description?: string; hierarchy?: number; permissions?: string[] }): Promise<{ role: Role }> {
    return this.request<{ role: Role }>(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteRole(id: string): Promise<void> {
    return this.request<void>(`/roles/${id}`, {
      method: 'DELETE'
    });
  }

  // Product endpoints
  async getProducts(page?: number, filters?: ProductFilters, limit?: number, viewAllOrgs?: boolean): Promise<ProductListResponse> {
    const params = new URLSearchParams();
    if (page) params.set('page', page.toString());
    if (limit) params.set('limit', limit.toString());
    if (viewAllOrgs) params.set('viewAllOrgs', 'true');
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.organizationId) params.set('organizationId', filters.organizationId);

    const queryString = params.toString();
    return this.request(`/products${queryString ? `?${queryString}` : ''}`);
  }

  async getProductById(id: string): Promise<{ product: Product }> {
    return this.request(`/products/${id}`);
  }

  async createProduct(data: CreateProductRequest): Promise<{ product: Product }> {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id: string, data: UpdateProductData): Promise<{ product: Product }> {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id: string): Promise<{ message: string }> {
    return this.request(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  // Document Template API methods
  async getDocumentTemplates(filters?: DocumentTemplateFilters): Promise<{ templates: DocumentTemplate[], pagination: { total: number, page: number, totalPages: number, limit: number } }> {
    const params = new URLSearchParams();
    if (filters?.page) params.set('page', filters.page.toString());
    if (filters?.limit) params.set('limit', filters.limit.toString());
    if (filters?.search) params.set('search', filters.search);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.tags?.length) params.set('tags', filters.tags.join(','));

    const queryString = params.toString();
    return this.request(`/document-templates${queryString ? `?${queryString}` : ''}`);
  }

  async getDocumentTemplateById(id: string): Promise<{ template: DocumentTemplate }> {
    return this.request(`/document-templates/${id}`);
  }

  async createDocumentTemplate(data: FormData): Promise<{ template: DocumentTemplate }> {
    return this.request('/document-templates', {
      method: 'POST',
      body: data,
    });
  }

  async updateDocumentTemplate(id: string, data: Partial<DocumentTemplate>): Promise<{ template: DocumentTemplate }> {
    return this.request(`/document-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDocumentTemplate(id: string): Promise<{ message: string }> {
    return this.request(`/document-templates/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadDocumentTemplateVersion(id: string, data: FormData): Promise<{ version: DocumentTemplateVersion }> {
    return this.request(`/document-templates/${id}/versions`, {
      method: 'POST',
      body: data,
    });
  }

  async downloadDocumentTemplate(id: string): Promise<{ blob: Blob; filename: string }> {
    return this.requestBlobWithFilename(`/document-templates/${id}/download`);
  }

  async downloadQuoteTemplate(id: string, type: string = 'simplified'): Promise<{ blob: Blob; filename: string }> {
    return this.requestBlobWithFilename(`/quotes/${id}/template?type=${type}`);
  }

  // Quote document generation methods
  async generateQuoteDocument(id: string): Promise<{ success: boolean; pdfUrl: string; filename: string; message: string }> {
    return this.request(`/quotes/${id}/documents/quote`);
  }

  async generatePolicyDocument(id: string): Promise<{ success: boolean; pdfUrl: string; filename: string; message: string }> {
    return this.request(`/quotes/${id}/documents/policy`);
  }

  async generateInvoiceDocument(id: string): Promise<{ success: boolean; pdfUrl: string; filename: string; message: string }> {
    return this.request(`/quotes/${id}/documents/invoice`);
  }

  // Get generated documents for a quote
  async getGeneratedDocuments(quoteId: string): Promise<{ documents: GeneratedDocument[] }> {
    return this.request(`/quotes/${quoteId}/generated-documents`);
  }

  // Get generated documents count for a quote by type
  async getGeneratedDocumentsCount(quoteId: string): Promise<{ counts: { quote: number; policy: number; invoice: number; total: number } }> {
    return this.request(`/quotes/${quoteId}/generated-documents/count`);
  }

  // View/download document
  viewDocument(bucket: string, filename: string): void {
    const url = `${API_BASE_URL}/documents/download/${bucket}/${filename}`;
    window.open(url, '_blank');
  }

  // Delete document
  async deleteDocument(documentId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/documents/${documentId}`, {
      method: 'DELETE'
    });
  }

  async getDocumentTemplateStats(id: string): Promise<{ stats: DocumentTemplateStats }> {
    return this.request(`/document-templates/${id}/stats`);
  }

  async getDocumentTemplateTypes(): Promise<{ types: string[] }> {
    return this.request('/document-templates/types');
  }

  // Farmer endpoints
  async getFarmers(options?: {
    page?: number;
    limit?: number;
    search?: string;
    county?: string;
    crop?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    adminCode?: string;
    adminLevel?: string;
    organizationId?: string;
    isActive?: boolean;
    viewAllOrgs?: boolean;
  }): Promise<GetFarmersResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.set('page', options.page.toString());
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.search) params.set('search', options.search);
    if (options?.county) params.set('county', options.county);
    if (options?.crop) params.set('crop', options.crop);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.adminCode) params.set('adminCode', options.adminCode);
    if (options?.adminLevel) params.set('adminLevel', options.adminLevel);
    if (options?.organizationId) params.set('organizationId', options.organizationId);
    if (options?.isActive !== undefined) params.set('isActive', options.isActive.toString());
    if (options?.viewAllOrgs) params.set('viewAllOrgs', 'true');

    const queryString = params.toString();
    return this.request(`/farmers${queryString ? `?${queryString}` : ''}`);
  }

  async getFarmerById(id: string): Promise<GetFarmerResponse> {
    return this.request(`/farmers/${id}`);
  }

  async createFarmer(farmerData: FarmerFormData): Promise<CreateFarmerResponse> {
    return this.request('/farmers', {
      method: 'POST',
      body: JSON.stringify(farmerData),
    });
  }

  async updateFarmer(id: string, farmerData: Partial<FarmerFormData>): Promise<UpdateFarmerResponse> {
    return this.request(`/farmers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(farmerData),
    });
  }

  async deleteFarmer(id: string, hardDelete: boolean = false): Promise<{ message: string }> {
    const url = hardDelete ? `/farmers/${id}?hardDelete=true` : `/farmers/${id}`;
    return this.request(url, {
      method: 'DELETE',
    });
  }

  // Quote endpoints
  async getQuotes(
    page: number = 1, 
    filters: QuoteFilters = {}, 
    limit: number = 25,
    viewAllOrgs: boolean = false
  ): Promise<QuoteListResponse> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    
    if (viewAllOrgs) params.set('viewAllOrgs', 'true');
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.productId) params.set('productId', filters.productId);
    if (filters.participantId) params.set('participantId', filters.participantId);
    if (filters.isTemplate !== undefined) params.set('isTemplate', filters.isTemplate.toString());
    if (filters.coveragePeriod?.start) params.set('coverageStart', filters.coveragePeriod.start);
    if (filters.coveragePeriod?.end) params.set('coverageEnd', filters.coveragePeriod.end);

    const queryString = params.toString();
    return this.request(`/quotes${queryString ? `?${queryString}` : ''}`);
  }

  async getQuoteStatistics(viewAllOrgs: boolean = false): Promise<ApiResponse<{
    totalQuotes: number;
    draftQuotes: number;
    approvedQuotes: number;
    readyForPaymentQuotes: number;
    paymentInitiatedQuotes: number;
  }>> {
    const params = new URLSearchParams();
    if (viewAllOrgs) params.set('viewAllOrgs', 'true');
    
    const queryString = params.toString();
    return this.request(`/quotes/statistics${queryString ? `?${queryString}` : ''}`);
  }

  async getQuoteById(id: string): Promise<QuoteDetailResponse> {
    return this.request(`/quotes/${id}`);
  }

  async getComprehensiveQuoteById(id: string): Promise<import('../types/comprehensiveQuote').ComprehensiveQuoteData> {
    return this.request(`/quotes/${id}/comprehensive`);
  }

  async createDraftQuote(data: CreateDraftQuoteRequest): Promise<QuoteDetailResponse> {
    return this.request('/quotes/draft', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createQuote(data: CreateQuoteRequest): Promise<QuoteDetailResponse> {
    return this.request('/quotes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQuote(id: string, data: UpdateQuoteRequest): Promise<QuoteDetailResponse> {
    return this.request(`/quotes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getQuotationSummary(id: string, protectionLevels?: Record<string, unknown>): Promise<QuotationSummaryResponse> {
    return this.request(`/quotes/${id}/quotation-summary`, {
      method: 'POST',
      body: protectionLevels ? JSON.stringify({ protectionLevels }) : undefined,
    });
  }

  async deleteQuote(id: string): Promise<{ message: string }> {
    return this.request(`/quotes/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteDraftQuote(id: string): Promise<{ success: boolean; message: string; data: { deleted: boolean; quoteId: string } }> {
    return this.request(`/quotes/${id}/draft`, {
      method: 'DELETE',
    });
  }

  // Farmer document upload
  async uploadFarmerDocument(file: File, documentType: string, farmerId?: string): Promise<{ document: FarmerDocument }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (farmerId) {
      formData.append('farmerId', farmerId);
    }

    return this.request('/farmers/documents/upload', {
      method: 'POST',
      body: formData,
    });
  }

  // Farmer document delete
  async deleteFarmerDocument(farmerId: string, documentId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/farmers/${farmerId}/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  // Farmer bulk upload template download
  async downloadFarmerBulkUploadTemplate(): Promise<{ blob: Blob; filename: string }> {
    return this.requestBlobWithFilename('/farmers/bulk-upload/template');
  }

  // Quote bulk upload template download
  async downloadQuoteBulkUploadTemplate(): Promise<{ blob: Blob; filename: string }> {
    return this.requestBlobWithFilename('/quotes/bulk-upload/template');
  }

  // Quotation endpoints
  async submitQuotationRequest(data: Record<string, unknown>): Promise<{ jobId: string; status: string; estimatedDuration?: number }> {
    return this.request('/quotation/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getQuotationJobStatus(jobId: string): Promise<{ status: string; [key: string]: unknown }> {
    return this.request(`/quotation/status/${jobId}`);
  }

  async getQuotationResults(jobId: string): Promise<{ [key: string]: unknown }> {
    return this.request(`/quotation/results/${jobId}`);
  }

  // Administrative Units API methods
  async getAdministrativeUnits(params?: { 
    adminLevel?: number; 
    countryCode?: string; 
    adminCode?: string; 
    parentCode?: string; 
  }): Promise<{ administrativeUnits: AdministrativeUnit[]; pagination: { page: number; limit: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean } }> {
    const searchParams = new URLSearchParams();
    if (params?.adminLevel !== undefined) searchParams.set('adminLevel', params.adminLevel.toString());
    if (params?.countryCode) searchParams.set('countryCode', params.countryCode);
    if (params?.adminCode) searchParams.set('adminCode', params.adminCode);
    if (params?.parentCode) searchParams.set('parentCode', params.parentCode);
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/administrative-units?${queryString}` : '/administrative-units';
    
    return this.request(endpoint);
  }

  async getAdministrativeUnitById(id: string): Promise<{ data: AdministrativeUnit }> {
    return this.request(`/administrative-units/${id}`);
  }

  async getAdministrativeUnitHierarchy(adminCode: string): Promise<{ 
    adminCode: string; 
    hierarchyPath: Record<number, string>; 
    displayName: string; 
  }> {
    return this.request(`/administrative-units/${adminCode}/hierarchy`);
  }

  // Ledger API methods
  async getLedgerJournals(params: Record<string, unknown> = {}): Promise<unknown> {
    const searchParams = new URLSearchParams();
    if (params.cursor) searchParams.append('cursor', params.cursor as string);
    if (params.limit) searchParams.append('limit', (params.limit as number).toString());
    if (params.periodName) searchParams.append('periodName', params.periodName as string);
    if (params.status) searchParams.append('status', params.status as string);
    if (params.externalRef) searchParams.append('externalRef', params.externalRef as string);

    const queryString = searchParams.toString();
    const endpoint = `/ledger/journals${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getLedgerJournalById(id: string): Promise<unknown> {
    return this.request(`/ledger/journals/${id}`);
  }

  async postLedgerJournalBatch(request: Record<string, unknown>, idempotencyKey: string): Promise<unknown> {
    return this.request(`/ledger/journals/batch`, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
  }

  async reverseLedgerJournal(journalId: string, reason: string, idempotencyKey: string): Promise<unknown> {
    return this.request(`/ledger/journals/${journalId}/reverse`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
  }

  async getLedgerTrialBalance(params: Record<string, unknown>): Promise<unknown> {
    const searchParams = new URLSearchParams();
    if (params.periodName) searchParams.append('periodName', params.periodName as string);
    if (params.asOfDate) searchParams.append('asOfDate', params.asOfDate as string);

    const queryString = searchParams.toString();
    const endpoint = `/ledger/reports/trial-balance${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getLedgerAccountLedger(accountCode: string, params: Record<string, unknown> = {}): Promise<unknown> {
    const searchParams = new URLSearchParams();
    if (params.cursor) searchParams.append('cursor', params.cursor as string);
    if (params.limit) searchParams.append('limit', (params.limit as number).toString());
    if (params.startDate) searchParams.append('startDate', params.startDate as string);
    if (params.endDate) searchParams.append('endDate', params.endDate as string);

    const queryString = searchParams.toString();
    const endpoint = `/ledger/reports/account-ledger/${accountCode}${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getLedgerChartOfAccounts(): Promise<unknown> {
    return this.request('/ledger/reports/chart-of-accounts');
  }

  async getLedgerPeriods(): Promise<unknown> {
    return this.request('/ledger/reports/periods');
  }

  // Insurance Premium Ledger API Methods

  // Premium lifecycle
  async recordPremiumDue(data: PremiumDueRequest, idempotencyKey: string): Promise<InsuranceJournal> {
    return this.request('/ledger/insurance/premium/due', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
  }

  async recordPaymentReceived(data: PaymentReceivedRequest, idempotencyKey: string): Promise<InsuranceJournal> {
    return this.request('/ledger/insurance/payment/received', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
  }

  async recordPremiumEarning(data: PremiumEarningRequest, idempotencyKey: string): Promise<InsuranceJournal> {
    return this.request('/ledger/insurance/premium/earn', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
  }

  // Client data
  async getClients(): Promise<Client[]> {
    // Use existing farmers endpoint as clients
    const response = await this.request('/farmers') as { farmers: unknown[] };
    return response.farmers?.map((farmer: unknown) => {
      const f = farmer as Record<string, unknown>;
      return {
        id: f.id as string,
        name: f.name as string,
        code: f.farmerCode as string,
        email: f.email as string,
        phone: f.phone as string,
        isActive: f.isActive as boolean
      };
    }) || [];
  }

  async getCostCenters(): Promise<CostCenter[]> {
    // This would need to be implemented in the backend
    // For now, return empty array
    return [];
  }

  // Reporting
  async getClientReceivablesAging(asOfDate?: Date): Promise<ClientReceivablesAging[]> {
    const params = asOfDate ? `?asOfDate=${asOfDate.toISOString()}` : '';
    return this.request(`/ledger/insurance/reports/aging${params}`);
  }

  async getClientBalanceSummary(clientId: string, periodId: string): Promise<ClientBalance[]> {
    return this.request(`/ledger/insurance/clients/${clientId}/balance?periodId=${periodId}`);
  }

  async getMultiClientBalances(data: { clientIds?: string[]; periodId?: string; accountLogicalKeys?: string[] }): Promise<ClientBalanceSummary[]> {
    // If no specific clients provided, get all clients for the period
    if (!data.clientIds && data.periodId) {
      const params = new URLSearchParams();
      params.append('periodId', data.periodId);
      return this.request(`/ledger/insurance/reports/client-summary?${params.toString()}`);
    }
    
    return this.request('/ledger/insurance/clients/balances/bulk', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getClientTransactionHistory(clientId: string, options: ClientTransactionOptions): Promise<CursorPaginationResponse<ClientTransaction>> {
    const searchParams = new URLSearchParams();
    if (options.costCenterId) searchParams.append('costCenterId', options.costCenterId);
    if (options.accountId) searchParams.append('accountId', options.accountId);
    if (options.fromDate) searchParams.append('fromDate', options.fromDate.toISOString());
    if (options.toDate) searchParams.append('toDate', options.toDate.toISOString());
    if (options.businessEventTypes?.length) {
      options.businessEventTypes.forEach(type => searchParams.append('businessEventTypes', type));
    }
    if (options.currency) searchParams.append('currency', options.currency);
    if (options.cursor) searchParams.append('cursor', options.cursor);
    if (options.limit) searchParams.append('limit', options.limit.toString());
    if (options.direction) searchParams.append('direction', options.direction);

    const queryString = searchParams.toString();
    return this.request(`/ledger/insurance/clients/${clientId}/transactions${queryString ? `?${queryString}` : ''}`);
  }

  // Batch processing
  async createPaymentBatch(data: PaymentBatchRequest): Promise<PaymentBatch> {
    return this.request('/ledger/insurance/batches', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getPaymentBatches(filters?: {
    provider?: string;
    status?: string;
    fromDate?: Date;
    toDate?: Date;
    currency?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ batches: PaymentBatch[]; pagination: { total: number; hasMore: boolean; nextCursor?: string } }> {
    const searchParams = new URLSearchParams();
    if (filters?.provider) searchParams.append('provider', filters.provider);
    if (filters?.status) searchParams.append('status', filters.status);
    if (filters?.fromDate) searchParams.append('fromDate', filters.fromDate.toISOString());
    if (filters?.toDate) searchParams.append('toDate', filters.toDate.toISOString());
    if (filters?.currency) searchParams.append('currency', filters.currency);
    if (filters?.limit) searchParams.append('limit', filters.limit.toString());
    if (filters?.offset) searchParams.append('offset', filters.offset.toString());

    const queryString = searchParams.toString();
    return this.request(`/ledger/insurance/batches${queryString ? `?${queryString}` : ''}`);
  }

  async processBatch(batchId: string): Promise<{ batch: PaymentBatch; journal: InsuranceJournal }> {
    return this.request(`/ledger/insurance/batches/${batchId}/process`, {
      method: 'POST'
    });
  }

  async getBatchReconciliation(batchId: string): Promise<{ reconciled: boolean; discrepancies: unknown[] }> {
    return this.request(`/ledger/insurance/batches/${batchId}/reconciliation`);
  }

  // Premium earning automation
  async getPremiumEarningCandidates(filters?: {
    organizationId?: string;
    currency?: string;
    minAmount?: string;
    maxAmount?: string;
  }): Promise<{ candidates: PremiumEarningCandidate[] }> {
    const params = new URLSearchParams();
    if (filters?.organizationId) params.append('organizationId', filters.organizationId);
    if (filters?.currency) params.append('currency', filters.currency);
    if (filters?.minAmount) params.append('minAmount', filters.minAmount);
    if (filters?.maxAmount) params.append('maxAmount', filters.maxAmount);
    
    const queryString = params.toString();
    return this.request(`/ledger/insurance/premium/earn/candidates${queryString ? `?${queryString}` : ''}`);
  }

  async processBulkPremiumEarning(data: BulkEarningRequest): Promise<{ processed: number; journals: InsuranceJournal[] }> {
    return this.request('/ledger/insurance/premium/earn/bulk', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Reconciliation reporting
  async getReconciliationReport(filters?: {
    periodId?: string;
    organizationId?: string;
  }): Promise<ReconciliationReport> {
    const params = new URLSearchParams();
    if (filters?.periodId) params.append('periodId', filters.periodId);
    if (filters?.organizationId) params.append('organizationId', filters.organizationId);
    
    const queryString = params.toString();
    return this.request(`/ledger/insurance/reconciliation${queryString ? `?${queryString}` : ''}`);
  }

  // Commission tracking
  async getCommissionSummary(filters?: {
    agentId?: string;
    currency?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<CommissionSummary> {
    const params = new URLSearchParams();
    if (filters?.agentId) params.append('agentId', filters.agentId);
    if (filters?.currency) params.append('currency', filters.currency);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    
    const queryString = params.toString();
    return this.request(`/ledger/insurance/commissions/summary${queryString ? `?${queryString}` : ''}`);
  }

  // Account management
  async initializeInsuranceMappings(): Promise<{ message: string }> {
    return this.request('/ledger/insurance/setup/mappings', {
      method: 'POST'
    });
  }

  async getAccountMappings(): Promise<AccountMapping[]> {
    return this.request('/ledger/insurance/mappings');
  }

  // State Machine API methods
  async getQuoteState(quoteId: string): Promise<{
    processKey: string;
    state: string;
    version: number;
    businessKey: string;
    nextEvents: string[];
  }> {
    return this.request(`/v1/quotes/${quoteId}/state`);
  }

  async transitionQuote(quoteId: string, data: {
    event: string;
    payload?: unknown;
    expectedVersion?: number;
    requestId?: string;
  }): Promise<{
    processKey: string;
    definitionVersion: number;
    state: string;
    version: number;
    businessKey: string;
    nextEvents?: string[];
  }> {
    return this.request(`/v1/quotes/${quoteId}/transition`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getQuoteHistory(quoteId: string, limit = 50): Promise<{
    quoteId: string;
    history: Array<{
      event: string;
      fromState: string;
      toState: string;
      at: Date;
      actorId?: string;
      payload?: unknown;
      requestId: string;
    }>;
    count: number;
  }> {
    return this.request(`/v1/quotes/${quoteId}/history?limit=${limit}`);
  }

  // Participant Payment Methods
  async getParticipantPayments(params: URLSearchParams): Promise<{
    payments: Array<{
      id: string;
      quoteNumber: string;
      participantName: string;
      totalAmountCents: number;
      paidAmountCents: number;
      remainingAmountCents: number;
      status: string;
      [key: string]: unknown;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.request(`/participant-payments?${params.toString()}`);
  }

  async getParticipantPaymentById(id: string): Promise<{
    id: string;
    quoteNumber: string;
    participantName: string;
    totalAmountCents: number;
    paidAmountCents: number;
    remainingAmountCents: number;
    status: string;
    installments: Array<{
      id: string;
      amountCents: number;
      status: string;
      transactionId?: string;
      processedAt?: string;
      createdAt: string;
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  }> {
    return this.request(`/participant-payments/${id}`);
  }

  async retryParticipantPayment(id: string): Promise<{
    message: string;
    authorizationId: string;
    status: string;
  }> {
    return this.request(`/participant-payments/${id}/retry`, {
      method: 'POST'
    });
  }

  // Claims Assessment Methods
  async getClaimsEligibleQuotes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    viewAllOrgs?: boolean;
  }): Promise<{
    quotes: Array<{
      id: string;
      quoteNumber: string;
      state: string;
      coverageStartDate: string;
      coverageEndDate: string;
      sumInsured: number;
      premiumAmount: number;
      participantCount: number;
      productName?: string;
      organizationName?: string;
      lastAssessmentDate?: string;
      hasAssessments: boolean;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.viewAllOrgs) searchParams.set('viewAllOrgs', 'true');
    
    const queryString = searchParams.toString();
    return this.request(`/claims${queryString ? `?${queryString}` : ''}`);
  }

  async getClaimsStatistics(): Promise<{
    assessments: {
      calculated: number;
      approved: number;
      paidOut: number;
    };
    quotesWithClaims: number;
    payoutAmounts: {
      calculated: number;
      approved: number;
      paidOut: number;
    };
  }> {
    return this.request('/claims/statistics');
  }

  async getClaimsForQuote(quoteId: string): Promise<{
    quote: {
      id: string;
      quoteNumber: string;
      state: string;
      coverageStartDate: string;
      coverageEndDate: string;
      sumInsured: number;
      premiumAmount: number;
      productName?: string;
      organizationName?: string;
    };
    participants: Array<{
      id: string;
      participantId: string;
      farmerName: string;
      farmerCode: string;
      sumInsured: number | null;
      latitude: number | null;
      longitude: number | null;
      runNumber: number;
      peril: string;
      payoutAmount: number;
      reading: number | null;
      eventDate: string;
      status: string;
      createdAt: string;
    }>;
  }> {
    return this.request(`/claims/quote/${quoteId}`);
  }

  async triggerClaimsAssessment(quoteId: string, data: {
    mock?: boolean;
    mockValue?: number;
    reportDetail?: 'minimal' | 'summary' | 'full';
    includeRawData?: boolean;
    claimsScript?: string;
    testMode?: boolean;
    testTriggers?: Record<string, unknown>;
  }): Promise<{
    mock: boolean;
    runNumber: number;
    message: string;
    assessmentsCreated?: number;
    mockValue?: number;
    jobId?: string;
    estimatedDuration?: number;
  }> {
    return this.request(`/claims/quote/${quoteId}/assess`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getClaimsJobStatus(jobId: string): Promise<{
    jobId: string;
    status: string;
    errorDetails?: string;
    quoteId: string;
  }> {
    return this.request(`/claims/job/${jobId}/status`);
  }

  async getClaimsJobResults(jobId: string): Promise<{
    jobId: string;
    status: string;
    payoutsProcessed?: number;
    quoteId?: string;
    message: string;
    errorDetails?: string;
  }> {
    return this.request(`/claims/job/${jobId}/results`);
  }

  async approveClaim(claimId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/claims/${claimId}/approve`, {
      method: 'PATCH'
    });
  }

  async bulkApproveClaims(claimIds: string[]): Promise<{
    success: boolean;
    message: string;
    approvedCount: number;
  }> {
    return this.request('/claims/bulk-approve', {
      method: 'POST',
      body: JSON.stringify({ ids: claimIds })
    });
  }

  // Claims Payments API
  async getClaimsPayments(params?: {
    page?: number;
    limit?: number;
    state?: string;
    viewAllOrgs?: boolean;
  }): Promise<{
    payouts: Array<{
      id: string;
      claimsAssessmentId: string;
      quoteId: string;
      participantId: string;
      amount: number;
      currency: string;
      state: string;
      processedAt: string | null;
      failureReason: string | null;
      createdAt: string;
      claimsAssessment: {
        id: string;
        peril: string;
        runNumber: number;
        reading: number | null;
        eventDate: string | null;
      };
      quote: {
        id: string;
        quoteNumber: string;
        product: { id: string; name: string };
        organization: { id: string; name: string };
      };
      participant: {
        id: string;
        farmer: {
          id: string;
          name: string;
          farmerCode: string;
        } | null;
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.state) searchParams.set('state', params.state);
    if (params?.viewAllOrgs) searchParams.set('viewAllOrgs', 'true');
    
    const query = searchParams.toString();
    return this.request(`/claims/payments${query ? `?${query}` : ''}`);
  }

  async getClaimsPaymentStatistics(viewAllOrgs?: boolean): Promise<{
    counts: {
      pending: number;
      paid: number;
      failed: number;
      total: number;
    };
    amounts: {
      pending: number;
      paid: number;
      failed: number;
    };
  }> {
    const query = viewAllOrgs ? '?viewAllOrgs=true' : '';
    return this.request(`/claims/payments/statistics${query}`);
  }

  async queueClaimsForPayment(claimIds: string[]): Promise<{
    queuedCount: number;
    payouts: Array<{
      id: string;
      claimsAssessmentId: string;
      amount: number;
      state: string;
    }>;
  }> {
    return this.request('/claims/payments/queue', {
      method: 'POST',
      body: JSON.stringify({ ids: claimIds })
    });
  }

  async executeClaimsPayments(payoutIds: string[]): Promise<{
    processedCount: number;
    message: string;
  }> {
    return this.request('/claims/payments/execute', {
      method: 'POST',
      body: JSON.stringify({ ids: payoutIds })
    });
  }

  // Automation Schedule API methods

  async getAutomationSchedules(resourceType: string, resourceId: string): Promise<{
    schedules: Array<{
      id: string;
      resourceType: string;
      resourceId: string;
      taskType: string;
      cronExpression: string;
      enabled: boolean;
      lastRunAt: string | null;
      nextRunAt: string | null;
      lastRunStatus: string | null;
      lastRunDetails: Record<string, unknown> | null;
    }>;
  }> {
    return this.request(`/automation/schedules?resourceType=${resourceType}&resourceId=${resourceId}`);
  }

  async upsertAutomationSchedule(data: {
    resourceType: string;
    resourceId: string;
    taskType: string;
    cronExpression: string;
    enabled?: boolean;
  }): Promise<{ schedule: { id: string; cronExpression: string; enabled: boolean } }> {
    return this.request('/automation/schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAutomationSchedule(id: string): Promise<{ message: string }> {
    return this.request(`/automation/schedules/${id}`, {
      method: 'DELETE',
    });
  }

  // Audit log endpoints
  async getAuditLogs(params: Record<string, string>): Promise<{
    logs: unknown[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/audit?${qs}`);
  }

  async getAuditLogById(id: string): Promise<unknown> {
    return this.request(`/audit/${id}`);
  }

  async getAuditFilters(): Promise<{ actions: string[]; resources: string[] }> {
    return this.request('/audit/filters');
  }
}

export const apiClient = new ApiClient();
export { ApiError };
export type { BackendUser, LoginCredentials, SignupCredentials, LoginResponse }; 